'use client'

// DetectionOverlay — CCTV-style recognition brackets over the world while
// walking. Reads the detection mailbox (written ~10Hz by DetectionScanner)
// on its own rAF and mutates DOM styles directly: React only mounts this
// once, so tracking costs zero re-renders. Corner brackets + a lowercase
// word + a stable fake confidence, in the product's surveillance language.

import { useEffect, useRef } from 'react'
import { detection } from '../../lib/detectionState'

function makeBoxEl() {
  const root = document.createElement('div')
  root.className = 'kiwi-det'
  const label = document.createElement('span')
  label.className = 'kiwi-det-label'
  root.appendChild(label)
  return { root, label }
}

export function DetectionOverlay() {
  const rootRef = useRef(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return
    const els = new Map()
    let raf = 0
    let lastVersion = -1

    const tick = () => {
      raf = requestAnimationFrame(tick)
      if (detection.version === lastVersion) return
      lastVersion = detection.version
      const seen = new Set()
      for (const b of detection.boxes) {
        seen.add(b.id)
        let el = els.get(b.id)
        if (!el) {
          el = makeBoxEl()
          root.appendChild(el.root)
          els.set(b.id, el)
        }
        el.root.style.transform = `translate(${b.x.toFixed(1)}px, ${b.y.toFixed(1)}px)`
        el.root.style.width = `${b.w.toFixed(1)}px`
        el.root.style.height = `${b.h.toFixed(1)}px`
        el.root.style.opacity = Math.max(0.6, Math.min(1, 1.3 - b.dist / 26)).toFixed(2)
        el.root.dataset.gold = b.gold ? '1' : '0'
        el.label.textContent = b.word
      }
      for (const [id, el] of els) {
        if (!seen.has(id)) {
          el.root.remove()
          els.delete(id)
        }
      }
    }
    raf = requestAnimationFrame(tick)
    return () => {
      cancelAnimationFrame(raf)
      for (const [, el] of els) el.root.remove()
      els.clear()
    }
  }, [])

  return (
    <>
      <div ref={rootRef} className="fixed inset-0 z-30 pointer-events-none" aria-hidden="true" />
      <style>{`
        .kiwi-det {
          position: absolute;
          top: 0; left: 0;
          --det: #ffffff;
          border: 1px solid var(--det);
          transition: opacity 160ms ease;
          animation: kiwi-det-in 180ms ease-out;
        }
        .kiwi-det[data-gold='1'] { --det: #fde68a; }
        .kiwi-det-label {
          position: absolute;
          top: -16px; left: -1px;
          font-size: 10px;
          letter-spacing: 0.14em;
          color: var(--det);
          white-space: nowrap;
          text-shadow: 0 1px 2px rgba(0,0,0,0.8);
        }
        @keyframes kiwi-det-in { from { opacity: 0; } }
      `}</style>
    </>
  )
}

export default DetectionOverlay
